import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { connect } from 'react-redux';
import LoaderWidget from './Loader';
import {
  getUserOwnedVPs,
  bulkUpdateVPKeepStatus,
} from '../redux/valuePropositions/actions';
import { getVPLimit, getPlanLabel } from '../helpers/plan';

const getVpId = (vp) => vp.vpId ?? vp.VpId;
const getIsLimited = (vp) => !!(vp.isLimited ?? vp.IsLimited);
const getVpName = (vp) => vp.vpName ?? vp.VpName ?? vp.l2Descr ?? vp.L2Descr ?? `VP #${getVpId(vp)}`;
const getVpDate = (vp) =>
  vp.updateDate || vp.UpdateDate || vp.createdDate || vp.CreatedDate || vp.updatedDateTime || vp.UpdatedDateTime;

/**
 * Cap-up only: user gained VP slots and can unlock limited VPs into free slots.
 * Does not replace VPSelectionModal (downgrade / over-cap keep flow).
 */
export const VPUnlockModal = ({
  isOpen,
  toggleModal,
  onConfirm,
  planId,
  subscriptionRulesByPlan,
  userOwnedVPs,
  userOwnedVPsLoading,
  userOwnedVPsError,
  updatingVPKeepStatus,
  updateVPKeepStatusError,
  getUserOwnedVPs: getUserOwnedVPsAction,
  bulkUpdateVPKeepStatus: bulkUpdateVPKeepStatusAction,
}) => {
  const [selectedVPs, setSelectedVPs] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const hasInitiatedGetVPsRef = useRef(false);
  const previousIsOpenRef = useRef(false);
  const sawUpdatingRef = useRef(false);
  const awaitingUpdateRef = useRef(false);

  const vpLimit = getVPLimit(subscriptionRulesByPlan, planId);

  const { activeVPs, limitedVPs } = useMemo(() => {
    const list = userOwnedVPs || [];
    return {
      activeVPs: list.filter((vp) => !getIsLimited(vp)),
      limitedVPs: list.filter((vp) => getIsLimited(vp)),
    };
  }, [userOwnedVPs]);

  const activeCount = activeVPs.length;
  const slotsAvailable = Math.max(0, vpLimit - activeCount);
  const selectedCount = Object.values(selectedVPs).filter(Boolean).length;
  const newlySelectedCount = limitedVPs.filter((vp) => selectedVPs[getVpId(vp)] === true).length;

  useEffect(() => {
    if (!isOpen && previousIsOpenRef.current) {
      hasInitiatedGetVPsRef.current = false;
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isProcessing) return;
    if (!hasInitiatedGetVPsRef.current && !userOwnedVPsLoading && !userOwnedVPs) {
      hasInitiatedGetVPsRef.current = true;
      getUserOwnedVPsAction();
    }
  }, [isOpen, isProcessing, userOwnedVPsLoading, userOwnedVPs, getUserOwnedVPsAction]);

  useEffect(() => {
    if (!userOwnedVPs) return;
    const initial = {};
    userOwnedVPs.forEach((vp) => {
      const id = getVpId(vp);
      // Active stay selected; limited start unchecked so the user chooses unlocks.
      initial[id] = !getIsLimited(vp);
    });
    setSelectedVPs(initial);
  }, [userOwnedVPs]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedVPs({});
      setIsProcessing(false);
      awaitingUpdateRef.current = false;
      sawUpdatingRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (updatingVPKeepStatus) {
      sawUpdatingRef.current = true;
    }
  }, [updatingVPKeepStatus]);

  useEffect(() => {
    if (!awaitingUpdateRef.current) return;
    if (updateVPKeepStatusError) {
      awaitingUpdateRef.current = false;
      setIsProcessing(false);
      return;
    }
    if (sawUpdatingRef.current && !updatingVPKeepStatus) {
      awaitingUpdateRef.current = false;
      setIsProcessing(false);
      onConfirm?.();
    }
  }, [updatingVPKeepStatus, updateVPKeepStatusError, onConfirm]);

  const handleToggleLimited = (vpId) => {
    setSelectedVPs((prev) => {
      const nextChecked = !prev[vpId];
      if (nextChecked) {
        const nextNewlySelected =
          limitedVPs.filter((vp) => {
            const id = getVpId(vp);
            return id === vpId ? true : prev[id] === true;
          }).length;
        if (nextNewlySelected > slotsAvailable) {
          return prev;
        }
      }
      return { ...prev, [vpId]: nextChecked };
    });
  };

  const handleSelectLatestLimited = () => {
    const sorted = [...limitedVPs].sort((a, b) => {
      const ta = getVpDate(a) ? new Date(getVpDate(a)).getTime() : 0;
      const tb = getVpDate(b) ? new Date(getVpDate(b)).getTime() : 0;
      return tb - ta;
    });
    const next = {};
    activeVPs.forEach((vp) => {
      next[getVpId(vp)] = true;
    });
    limitedVPs.forEach((vp) => {
      next[getVpId(vp)] = false;
    });
    const toUnlock = Math.min(slotsAvailable, sorted.length);
    for (let i = 0; i < toUnlock; i++) {
      next[getVpId(sorted[i])] = true;
    }
    setSelectedVPs(next);
  };

  const handleConfirm = () => {
    if (!userOwnedVPs?.length) {
      onConfirm?.();
      return;
    }
    if (selectedCount > vpLimit || newlySelectedCount > slotsAvailable) {
      return;
    }
    setIsProcessing(true);
    awaitingUpdateRef.current = true;
    sawUpdatingRef.current = false;
    const vpUpdates = userOwnedVPs.map((vp) => {
      const vpId = getVpId(vp);
      return {
        vpId,
        isLimited: selectedVPs[vpId] !== true,
      };
    });
    bulkUpdateVPKeepStatusAction({ vps: vpUpdates });
  };

  const canConfirm =
    !!vpLimit &&
    selectedCount <= vpLimit &&
    newlySelectedCount <= slotsAvailable &&
    !isProcessing &&
    !updatingVPKeepStatus &&
    !userOwnedVPsLoading;

  if (userOwnedVPsLoading || !vpLimit) {
    return (
      <Modal isOpen={isOpen} toggle={toggleModal} size="lg" backdrop="static">
        <ModalHeader toggle={toggleModal}>Unlock Value Propositions</ModalHeader>
        <ModalBody>
          <LoaderWidget />
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={toggleModal} size="lg" backdrop="static">
      <ModalHeader toggle={toggleModal}>Unlock Value Propositions</ModalHeader>
      <ModalBody>
        {userOwnedVPsError && (
          <Alert color="danger" className="mb-3">
            Error loading Value Propositions: {userOwnedVPsError}
          </Alert>
        )}
        {updateVPKeepStatusError && (
          <Alert color="danger" className="mb-3">
            Error updating Value Propositions: {updateVPKeepStatusError}
          </Alert>
        )}

        <div className="mb-3">
          <p>
            Your <strong>{getPlanLabel(planId)}</strong> plan now allows up to{' '}
            <strong>{vpLimit}</strong> Value Propositions.
          </p>
          <p>
            You currently have <strong>{activeCount}</strong> active. You can unlock up to{' '}
            <strong>{slotsAvailable}</strong> more from your limited list.
          </p>
          <p className="text-muted mb-0">
            Active VPs stay selected. Check limited VPs to make them accessible.
          </p>
        </div>

        <div className="mb-3">
          <strong>
            Selected: {selectedCount} / {vpLimit} slots
            {newlySelectedCount > 0 && (
              <span className="text-muted ml-2">(+{newlySelectedCount} unlocking)</span>
            )}
          </strong>
        </div>

        {limitedVPs.length === 0 ? (
          <Alert color="info">You have no limited Value Propositions to unlock.</Alert>
        ) : (
          <>
            <div className="mb-3">
              <Button
                color="link"
                className="p-0"
                onClick={handleSelectLatestLimited}
                disabled={isProcessing || updatingVPKeepStatus || slotsAvailable === 0}
              >
                Select latest {Math.min(slotsAvailable, limitedVPs.length)} limited VPs
              </Button>
            </div>

            {activeCount > 0 && (
              <div className="mb-3">
                <h6 className="text-muted">Currently active (kept)</h6>
                <ul className="list-unstyled mb-0">
                  {activeVPs.map((vp) => {
                    const id = getVpId(vp);
                    return (
                      <li key={id} className="mb-2">
                        <FormGroup check>
                          <Label check className="text-muted">
                            <Input type="checkbox" checked disabled />
                            <span className="ml-2">{getVpName(vp)}</span>
                          </Label>
                        </FormGroup>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div>
              <h6 className="text-muted">Limited — select to unlock</h6>
              <ul className="list-unstyled mb-0">
                {limitedVPs.map((vp) => {
                  const id = getVpId(vp);
                  const checked = selectedVPs[id] === true;
                  const wouldExceed =
                    !checked && newlySelectedCount >= slotsAvailable;
                  return (
                    <li key={id} className="mb-2">
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={checked}
                            disabled={
                              isProcessing ||
                              updatingVPKeepStatus ||
                              (wouldExceed && !checked)
                            }
                            onChange={() => handleToggleLimited(id)}
                          />
                          <span className="ml-2">{getVpName(vp)}</span>
                        </Label>
                      </FormGroup>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={toggleModal} disabled={isProcessing || updatingVPKeepStatus}>
          Maybe later
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={!canConfirm || (limitedVPs.length > 0 && newlySelectedCount === 0)}
        >
          {updatingVPKeepStatus || isProcessing ? 'Saving...' : 'Unlock selected'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  subscriptionRulesByPlan: state.User?.subscriptionRulesByPlan,
  userOwnedVPs: state.ValueProposition?.userOwnedVPs,
  userOwnedVPsLoading: state.ValueProposition?.userOwnedVPsLoading,
  userOwnedVPsError: state.ValueProposition?.userOwnedVPsError,
  updatingVPKeepStatus: state.ValueProposition?.updatingVPKeepStatus,
  updateVPKeepStatusError: state.ValueProposition?.updateVPKeepStatusError,
});

const mapDispatchToProps = (dispatch) => ({
  getUserOwnedVPs: () => dispatch(getUserOwnedVPs()),
  bulkUpdateVPKeepStatus: (payload) => dispatch(bulkUpdateVPKeepStatus(payload.vps)),
});

export default connect(mapStateToProps, mapDispatchToProps)(VPUnlockModal);
